// Вкладки «Аудит» и «Отладка» админ-консоли: журнал аудита и debug-лог провайдеров.
// Выделено из admin.ts (аудит, этап 2 — декомпозиция).
import { state } from '../shared/state';
import { $ } from '../shared/index';

interface DebugLogEntry {
  ts: number;
  level?: string;
  provider?: string;
  message: string;
}

interface AuditLogEntry {
  created_at: string;
  action: string;
  username?: string;
  ip_address?: string;
  details?: string;
}

export async function loadAuditLogs() {
  const username = $<HTMLInputElement>('admin-audit-search')?.value.trim() || '';
  const action = $<HTMLSelectElement>('admin-audit-action')?.value || '';
  let url = '/api/admin/audit?limit=100';
  if (username) url += '&username=' + encodeURIComponent(username);
  if (action) url += '&action=' + encodeURIComponent(action);

  const r = await fetch(url, { headers: { 'Authorization': 'Bearer ' + state.authToken } });
  if (r.ok) {
    const logs = await r.json() as AuditLogEntry[];
    const list = $('admin-audit-list');
    if (!list) return;
    list.textContent = '';

    if (logs.length === 0) {
      list.innerHTML = '<div class="audit-log-empty">Логов не найдено</div>';
      return;
    }

    logs.forEach(log => {
      const date = new Date(log.created_at).toLocaleString('ru-RU');
      const el = document.createElement('div');
      el.className = 'user-item';
      let detailsHtml = '';
      if (log.details) {
        try {
          const parsed = JSON.parse(log.details);
          detailsHtml = `<div class="audit-log-details">${DOMPurify.sanitize(JSON.stringify(parsed))}</div>`;
        } catch {
          detailsHtml = `<div class="audit-log-details">${DOMPurify.sanitize(log.details)}</div>`;
        }
      }

      el.innerHTML = `
        <div class="user-item-info w-full">
          <div class="audit-log-header">
            <span class="user-item-name audit-log-name">${DOMPurify.sanitize(date)}</span>
            <span class="status-badge audit-log-action-badge">${DOMPurify.sanitize(log.action)}</span>
          </div>
          <div class="audit-log-content">
            <strong>Пользователь:</strong> ${log.username ? DOMPurify.sanitize(log.username) : `<span class="text-secondary">Система/Аноним</span>`}
            ${log.ip_address ? ` | <strong>IP:</strong> ${DOMPurify.sanitize(log.ip_address)}` : ''}
          </div>
          ${detailsHtml}
        </div>
      `;
      list.appendChild(el);
    });
  }
}

$('btn-refresh-audit')?.addEventListener('click', loadAuditLogs);
$('admin-audit-search')?.addEventListener('keypress', (e: KeyboardEvent) => {
  if (e.key === 'Enter') loadAuditLogs();
});
$('admin-audit-action')?.addEventListener('change', loadAuditLogs);

const debugLogBuffer: DebugLogEntry[] = [];
const MAX_DEBUG_ENTRIES = 200;

export function appendDebugLog(entry: DebugLogEntry) {
  debugLogBuffer.unshift(entry);
  if (debugLogBuffer.length > MAX_DEBUG_ENTRIES) debugLogBuffer.pop();
  const tab = document.querySelector('.admin-tab[data-tab="debug"]');
  if (tab && tab.classList.contains('active')) {
    renderDebugLogs();
  }
}

function renderDebugLogs() {
  const list = $('admin-debug-list');
  if (!list) return;
  if (debugLogBuffer.length === 0) {
    list.innerHTML = '<div class="audit-log-empty">Нет записей. Включите режим отладки в настройках категории.</div>';
    return;
  }
  list.innerHTML = '';
  debugLogBuffer.forEach(entry => {
    const el = document.createElement('div');
    el.className = 'user-item';
    const ts = new Date(entry.ts).toLocaleTimeString('ru-RU');
    const levelColor = entry.level === 'error' ? 'var(--color-error)' : entry.level === 'warn' ? 'orange' : 'var(--color-accent)';
    el.innerHTML = `
      <div class="user-item-info w-full">
        <div class="audit-log-header">
          <span class="user-item-name audit-log-name">${DOMPurify.sanitize(ts)}</span>
          <span class="status-badge" style="background:${levelColor}">${DOMPurify.sanitize(entry.level?.toUpperCase() || 'DEBUG')}</span>
          <span class="user-item-cat">${DOMPurify.sanitize(entry.provider || '')}</span>
        </div>
        <div class="audit-log-details" style="font-family:monospace;font-size:0.8em;white-space:pre-wrap">${DOMPurify.sanitize(entry.message)}</div>
      </div>
    `;
    list.appendChild(el);
  });
}

export function loadDebugLogs() {
  fetch('/api/admin/debug/stream', {
    headers: { 'Authorization': 'Bearer ' + state.authToken }
  }).then(r => r.ok ? r.json() : []).then((entries: DebugLogEntry[]) => {
    const list = $('admin-debug-list');
    if (!list) return;
    if (!entries || entries.length === 0) {
      list.innerHTML = '<div class="audit-log-empty">Нет записей. Включите режим отладки в настройках категории.</div>';
      return;
    }
    list.innerHTML = '';
    entries.forEach(entry => {
      const el = document.createElement('div');
      el.className = 'user-item';
      const ts = new Date(entry.ts).toLocaleTimeString('ru-RU');
      const levelColor = entry.level === 'error' ? 'var(--color-error)' : entry.level === 'warn' ? 'orange' : 'var(--color-accent)';
      el.innerHTML = `
        <div class="user-item-info w-full">
          <div class="audit-log-header">
            <span class="user-item-name audit-log-name">${DOMPurify.sanitize(ts)}</span>
            <span class="status-badge" style="background:${levelColor}">${DOMPurify.sanitize(entry.level?.toUpperCase() || 'DEBUG')}</span>
            <span class="user-item-cat">${DOMPurify.sanitize(entry.provider || '')}</span>
          </div>
          <div class="audit-log-details" style="font-family:monospace;font-size:0.8em;white-space:pre-wrap">${DOMPurify.sanitize(entry.message)}</div>
        </div>
      `;
      list.appendChild(el);
    });
  }).catch(e => console.error('Debug log load failed', e));
}

$('btn-refresh-debug')?.addEventListener('click', loadDebugLogs);
$('btn-clear-debug')?.addEventListener('click', async () => {
  await fetch('/api/admin/debug/log', {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + state.authToken }
  });
  loadDebugLogs();
});
